import { faAdd, faEdit, faSave, faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Form, Input, Popconfirm, Select, Space, Table, TableColumnType } from 'antd'
import { useState } from 'react'
import { hasValue } from '../../../helpers/nullable'
import VSpace from '../../common/VSpace'
import ToggleablePopconfirm from '../Divisions/ToggleablePopconfirm'
import { useCostCodes } from "../../../domain/costCodes/costCodeQueries"
import { uniqueId } from '../../../helpers/uniqueId'
import { UnbillableCategory } from '../../../domain/unbillableCategories/unbillableCategory'
import { useDeleteUnbillableCategory, useUnbillableCategories, useUpdateUnbillableCategory } from '../../../domain/unbillableCategories/unbillableCategoryQueries'
import LabeledSwitch from '../../common/LabeledSwitch'
import { sortBy } from 'lodash'

const UnbillableCategories = () => {
  const unbillableCategoriesQuery = useUnbillableCategories()
  const updateUnbillableCategoryMutation = useUpdateUnbillableCategory()
  const deleteUnbillableCategoryMutation = useDeleteUnbillableCategory()

  const costCodesQuery = useCostCodes()

  const [form] = Form.useForm()
  const [editingUnbillableCategoryId, setEditingUnbillableCategoryId] = useState<string | undefined>()
  const editingUnbillableCategory = unbillableCategoriesQuery.data?.find(c => c.id === editingUnbillableCategoryId)

  const [addedUnbillableCategory, setAddedUnbillableCategory] = useState<UnbillableCategory[]>([])
  const [addedUnbillableCategoryIds, setAddedUnbillableCategoryIds] = useState<string[]>([])
  const unbillableCategories = sortBy([...addedUnbillableCategory, ...(unbillableCategoriesQuery.data ?? [])], x => x.isDefault ? 0 : 1, x => x.name)

  const shouldConfirmAdd = () => hasValue(editingUnbillableCategoryId) && form.isFieldsTouched()
  const shouldConfirmEdit = (id: string) => hasValue(editingUnbillableCategoryId) && editingUnbillableCategoryId !== id && form.isFieldsTouched()
  const shouldConfirmCancel = () => form.isFieldsTouched()

  const handleAdd = () => {
    const id = uniqueId()
    const newUnbillableCategory: UnbillableCategory = {
      id,
      name: '',
      isDefault: false,
      costCodeId: undefined,
    }
    setAddedUnbillableCategory([newUnbillableCategory])
    setAddedUnbillableCategoryIds(s => [...s, id])
    form.setFieldsValue(newUnbillableCategory)
    setEditingUnbillableCategoryId(id)
  }

  const editUnbillableCategory = (id: string) => {
    if (editingUnbillableCategoryId === id) {
      cancelEdit()
    }
    setEditingUnbillableCategoryId(id)
    setTimeout(() => form.setFieldsValue(unbillableCategories.find(x => x.id === id)), 1)
  }

  const cancelEdit = () => {
    if (addedUnbillableCategoryIds.includes(editingUnbillableCategoryId!)) {
      setAddedUnbillableCategory([])
    }
    setEditingUnbillableCategoryId(undefined)
    form.resetFields()
  }

  const saveUnbillableCategory = async () => {
    try {
      await form.validateFields()
      updateUnbillableCategoryMutation.mutate(form.getFieldsValue())
      setEditingUnbillableCategoryId(undefined)
      setAddedUnbillableCategory([])
      form.resetFields()
    } catch { }
  }

  const handleDelete = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation()
    e.preventDefault()
    deleteUnbillableCategoryMutation.mutate(id)
  }

  const columns: TableColumnType<UnbillableCategory>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      render: (value, record) => {
        if (record.id === editingUnbillableCategoryId) {
          return <Form.Item name="name">
            <Input />
          </Form.Item>
        }

        return value
      }
    },
    {
      key: 'costCodeId',
      title: 'Cost Code',
      dataIndex: 'costCodeId',
      render: (_value, record) => {
        if (record.id !== editingUnbillableCategoryId) {
          const costCode = costCodesQuery.data?.find(x => x.id === record.costCodeId)
          return costCode?.name ?? ''
        }

        return <Form.Item name="costCodeId">
          <Select
            options={costCodesQuery.data?.map(x => ({ label: x.name, value: x.id }))}
            style={{ width: '100%' }}
          />
        </Form.Item>
      }
    },
    {
      key: 'isDefault',
      title: 'Default',
      dataIndex: 'isDefault',
      render: (value, record) => {
        if (record.id !== editingUnbillableCategoryId) {
          return value ? 'Yes' : 'No'
        }

        return <Form.Item name="isDefault">
          <LabeledSwitch label="Default" />
        </Form.Item>
      }
    },
    {
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const buttons: JSX.Element[] = []
        if (record.id === editingUnbillableCategoryId) {
          buttons.push(
            <Button
              key="save"
              type="link"
              onClick={saveUnbillableCategory}
              icon={<FontAwesomeIcon icon={faSave} />}
            />)
          buttons.push(
            <ToggleablePopconfirm
              title="Are you sure you want to discard your changes?"
              shouldConfirm={shouldConfirmCancel}
              onConfirm={cancelEdit}
              key="cancel"
            >
              <Button
                type="link"
                danger
                icon={<FontAwesomeIcon icon={faTimes} />}
              />
            </ToggleablePopconfirm>
          )
        } else {
          buttons.push(
            <ToggleablePopconfirm
              title="Another unbillable category is being edited. Are you sure you want to edit this one?"
              shouldConfirm={() => shouldConfirmEdit(record.id)}
              onConfirm={() => editUnbillableCategory(record.id)}
              key="edit"
            >
              <Button
                type="link"
                icon={<FontAwesomeIcon icon={faEdit} />}
              />
            </ToggleablePopconfirm>

          )
        }

        buttons.push(<Popconfirm
          key="delete"
          title="Are you sure want to delete this unbillable category?"
          onConfirm={e => handleDelete(e!, record.id)}
        >
          <Button
            danger
            type="link"
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        </Popconfirm>)

        return <Form.Item>
          <Space>
            {buttons}
          </Space>
        </Form.Item>
      }
    }
  ]

  return <>
    <VSpace>
      <ToggleablePopconfirm
        title="Another unbillable category is already being edited. Do you want to discard those changes?"
        shouldConfirm={shouldConfirmAdd}
        onConfirm={handleAdd}
      >
        <Button
          icon={<FontAwesomeIcon icon={faAdd} />}
          type="primary"
        >
          Add Unbillable Category
        </Button>
      </ToggleablePopconfirm>
      <Form initialValues={editingUnbillableCategory} form={form}>
        <Form.Item hidden name="id">
          <Input type="hidden" />
        </Form.Item>
        <Table
          rowKey="id"
          loading={unbillableCategoriesQuery.isLoading}
          columns={columns}
          dataSource={unbillableCategories}
          pagination={false}
        />
      </Form>
    </VSpace>
  </>
}

export default UnbillableCategories
